import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Loader2, Fingerprint, ThumbsUp, Flag, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";

const PILLARS = [
  { k: "safety", label: "Structural safety", color: "#F59E0B" },
  { k: "environment", label: "Environmental purity", color: "#00FF7F" },
  { k: "rent_fairness", label: "Land-rent fairness", color: "#00FFFF" },
  { k: "transparency", label: "Civic transparency", color: "#8A2BE2" },
];

export const CityVerdict = ({ city, weights, onClaimFiled }) => {
  const { user } = useAuth();
  const { lang } = useLang();
  const [claims, setClaims] = useState(null);
  const [explain, setExplain] = useState(null);
  const [kind, setKind] = useState("attestation");
  const [statement, setStatement] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setExplain(null); setClaims(null);
    api.get("/justice/claims", { params: { city: city.city } }).then((r) => setClaims(r.data)).catch(() => setClaims([]));
    api.post("/justice/explain", { city: city.city, lang }).then((r) => setExplain(r.data.explanation)).catch(() => setExplain("Explanation unavailable."));
  }, [city.city, city.verdict_hash, lang]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/justice/claims", { city: city.city, kind, statement, verdict_hash: city.verdict_hash });
      setClaims([data, ...(claims || [])]);
      setStatement("");
      toast.success(kind === "dispute" ? "Dispute anchored on the ledger" : "Attestation anchored on the ledger");
      onClaimFiled?.();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="qs-grad-border p-6 space-y-6" data-testid="city-verdict">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500">Verdict · rank {city.rank ?? "—"}</p>
          <h3 className="font-display font-bold text-3xl mt-1">{city.city}</h3>
        </div>
        <div className="text-right">
          <div className="font-mono text-5xl font-bold text-[#00FFFF]" data-testid="verdict-index">{city.index ?? "—"}</div>
          <div className="text-xs font-mono text-slate-500">grade {city.grade} · {city.coverage}/4 pillars</div>
        </div>
      </div>

      <div className="space-y-2.5">
        {PILLARS.map((p) => {
          const v = city.pillars[p.k];
          return (
            <div key={p.k} data-testid={`pillar-${p.k}`}>
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">{p.label} <span className="text-slate-600 font-mono">×{weights[p.k]}</span></span>
                <span className="font-mono" style={{ color: v == null ? "#475569" : p.color }}>{v ?? "no data"}</span>
              </div>
              <div className="h-1.5 bg-[#050A15] mt-1"><div className="h-full transition-all duration-700" style={{ width: `${v ?? 0}%`, background: p.color }} /></div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] font-mono" data-testid="evidence">
        {Object.entries(city.evidence).map(([k, v]) => (
          <div key={k} className="bg-[#050A15] border border-white/5 p-2"><div className="text-slate-200">{v}</div><div className="text-slate-600">{k.replace(/_/g, " ")}</div></div>
        ))}
      </div>

      <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500 break-all" data-testid="verdict-hash">
        <Fingerprint className="h-3.5 w-3.5 shrink-0 text-[#00FFFF]" /> {city.verdict_hash}
      </div>

      <div data-testid="verdict-explanation">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#F59E0B] mb-2"><Sparkles className="h-3.5 w-3.5" /> God's Eye explains · Claude Sonnet 5</div>
        {explain == null ? <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Reading the evidence…</div>
          : <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">{explain}</p>}
      </div>

      <div data-testid="claims">
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Community ledger · {claims?.length ?? 0} claim(s)</div>
        {user === null ? (
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-4" data-testid="claim-auth-loading"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking session…</div>
        ) : user ? (
          <form onSubmit={submit} className="space-y-2 mb-4" data-testid="claim-form">
            <div className="flex gap-1.5">
              {["attestation", "dispute"].map((k) => (
                <button type="button" key={k} data-testid={`claim-kind-${k}`} onClick={() => setKind(k)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs border transition-colors ${kind === k ? (k === "dispute" ? "border-[#EF4444] text-[#EF4444] bg-[#EF4444]/10" : "border-[#00FF7F] text-[#00FF7F] bg-[#00FF7F]/10") : "border-white/10 text-slate-400"}`}>
                  {k === "dispute" ? <Flag className="h-3.5 w-3.5" /> : <ThumbsUp className="h-3.5 w-3.5" />} {k}
                </button>
              ))}
            </div>
            <textarea data-testid="claim-statement" value={statement} onChange={(e) => setStatement(e.target.value)} rows={3} minLength={12} maxLength={1200} required
              placeholder={kind === "dispute" ? "What does this verdict get wrong? Cite what you saw." : "What does this verdict get right? Add your witness."}
              className="w-full bg-[#050A15] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#8A2BE2]" />
            <button data-testid="claim-submit" disabled={busy || statement.trim().length < 12}
              className="qs-cta px-4 py-2 text-sm font-semibold bg-[#8A2BE2] text-white disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Anchor on ledger"}
            </button>
          </form>
        ) : (
          <div className="flex items-center gap-2 border border-white/10 p-3 text-xs text-slate-400 mb-4" data-testid="claim-login-hint">
            <Lock className="h-3.5 w-3.5" /> <Link to="/login" className="text-[#00FFFF] hover:underline">Sign in</Link> to dispute or attest this verdict.
          </div>
        )}
        <div className="space-y-2 max-h-72 overflow-auto">
          {claims === null ? <Loader2 className="h-4 w-4 animate-spin text-slate-500" /> : claims.length === 0
            ? <p className="text-xs text-slate-600">No claims yet — be the first witness.</p>
            : claims.map((c) => (
              <div key={c.id} data-testid={`claim-${c.id}`} className={`border-l-2 pl-3 py-1 ${c.kind === "dispute" ? "border-[#EF4444]" : "border-[#00FF7F]"}`}>
                <div className="flex justify-between text-[11px] font-mono text-slate-500">
                  <span className={c.kind === "dispute" ? "text-[#EF4444]" : "text-[#00FF7F]"}>{c.kind} · {c.author} · {c.author_role}{c.target_type && c.target_type !== "city" && <span className="text-[#F59E0B]"> · {c.target_type}</span>}</span>
                  <span>{new Date(c.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-sm text-slate-300 mt-0.5">{c.statement}</p>
                <div className="text-[10px] font-mono text-slate-600 truncate">anchor {c.anchor}</div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};
